import { inject } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpRequest } from '@angular/common/http';
import { EMPTY, filter, Observable, of, switchMap, throwError } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';
import { TokenService } from '../token-service/token.service';
import { AuthService, DISABLE_AUTH_HEADER, SessionEndReason } from '../auth-service/auth.service';
import { URLService } from '../url-service/url.service';
import { ToastService } from '../toast-service/toast.service';
import { MsalAuthService } from '../msal-auth-service/msal-auth.service';

/**
 * A 401 carrying no body is an expired credential worth renewing; one carrying a body is a refusal
 * the caller should see. A staff session must renew against the directory, so it never falls through
 * to the password path: an unconfigured application cannot renew it at all and is signed out instead.
 * A refusal answered to a credential that is no longer the stored one belongs to a session already
 * replaced or cleared, so it never ends the session that took its place. A customer whose token is
 * refused is sent back to verify their link. Only a refused renewal is reported as such, so a session
 * opened from an external launch can re-launch there rather than at a sign-in its user cannot use.
 * The renewal's error handler wraps the renewal alone: a replayed request that fails after a
 * successful renewal is an ordinary error for its caller, not a reason to end a working session.
 */
export function authInterceptor(req: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> {
    const tokenService = inject(TokenService);
    const authService = inject(AuthService);
    const toastService = inject(ToastService);
    const urlService = inject(URLService);
    const msalAuthService = inject(MsalAuthService, { optional: true });

    const token = tokenService.getToken();
    const refreshToken = tokenService.getRefreshToken();
    const serverUrl = urlService.getURL();

    if (token && !req.context.get(DISABLE_AUTH_HEADER)) {
        req = req.clone({
            setHeaders: {
                Authorization: 'Bearer ' + token,
            },
        });
    }

    if (!req.headers.has('Content-Type') && !(req.body instanceof FormData)) {
        req = req.clone({
            setHeaders: {
                'content-type': 'application/json',
            },
        });
    }

    if (!req.headers.has('Federation-Server-Url')) {
        req = req.clone({
            setHeaders: {
                'Federation-Server-Url': serverUrl,
            },
        });
    }

    req = req.clone({
        headers: req.headers.set('Accept', 'application/json'),
    });

    const endSession = async (reason: SessionEndReason = SessionEndReason.SignedOut) => {
        await toastService.displayToast('Your session has ended.', 'warning', 'warning-outline', true, 1500);
        await authService.logout(reason);
    };

    const returnToCustomerLink = async () => {
        await toastService.displayToast('Your session has ended. Please verify again to continue.', 'warning', 'warning-outline', true, 4000);
        await authService.returnToCustomerLink();
    };

    return next(req).pipe(
        map((event: HttpEvent<unknown>) => event),
        catchError((error: HttpErrorResponse) => {
            if (!error?.url?.includes('authentication')) {
                if (error?.status === 401) {
                    if (error.error === null && refreshToken) {
                        if (authService.isStaffSession()) {
                            if (!msalAuthService?.isConfigured()) {
                                endSession();
                                return of();
                            }

                            return authService.staffRefreshToken(refreshToken).pipe(
                                filter((res) => res !== undefined),
                                take(1),
                                catchError((staffRefreshError: unknown) => {
                                    console.error('Staff session renewal failed:', staffRefreshError);
                                    endSession(SessionEndReason.RenewalRefused);
                                    return EMPTY;
                                }),
                                switchMap((data) => {
                                    return next(
                                        req.clone({
                                            setHeaders: {
                                                Authorization: `Bearer ${data.access_token}`,
                                            },
                                        }),
                                    );
                                }),
                            );
                        }

                        return authService.refreshToken(refreshToken).pipe(
                            filter((res) => res !== undefined),
                            take(1),
                            catchError((refreshTokenError: HttpErrorResponse) => {
                                if (refreshTokenError instanceof ErrorEvent) {
                                    console.error('An error occurred:', refreshTokenError);
                                } else {
                                    console.error(`Backend returned code ${refreshTokenError.status}, ` + `body was: ${refreshTokenError.message}`);
                                }
                                endSession(SessionEndReason.RenewalRefused);
                                return EMPTY;
                            }),
                            switchMap((data) => {
                                return next(
                                    req.clone({
                                        setHeaders: {
                                            Authorization: `Bearer ${data.access_token}`,
                                        },
                                    }),
                                );
                            }),
                        );
                    } else if (authService.isLoggedIn() && tokenService.getToken() === token) {
                        endSession();
                    } else if (error.error === null && tokenService.getToken() === token && authService.customerLinkId()) {
                        returnToCustomerLink();
                        return of();
                    }
                }
            }
            return throwError(() => error);
        }),
    );
}
